import { useCallback, useEffect, useMemo, useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

import {
  administrationApi,
  type AdminDirectoryUser,
  type OrgInviteListItem,
} from "@/features/administration/api/administration.api";
import { InviteOrgMemberDialog } from "@/features/administration/components/InviteOrgMemberDialog";
import { EditOrgMemberDialog } from "@/features/administration/components/EditOrgMemberDialog";
import { canOfferSendResetLink } from "@/features/administration/components/resetLinkOffer";
import { SendResetLinkDialog } from "@/features/administration/components/SendResetLinkDialog";
import { ManageTeamMembershipDialog } from "@/features/administration/components/ManageTeamMembershipDialog";
import { RoleSelector } from "@/components/admin/RoleSelector";
import { Button } from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/overlay/Modal";
import { LEGACY_ORG_ROLE, normalizePlatformRole, PLATFORM_ROLE } from "@/utils/roles";
import type { OrgRoleUi } from "@/lib/auth/auth.types";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";
import { pluralize } from "@/utils/pluralize";
import { useAuth } from "@/state/AuthContext";

type PeopleStatus = "Active" | "Pending" | "Suspended" | "Expired";

function formatRelative(iso?: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function formatAbsolute(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function initialsFromName(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (email || "?").slice(0, 2).toUpperCase();
}

function initials(user: AdminDirectoryUser): string {
  return initialsFromName(user.name, user.email);
}

function toDropdownRole(member: AdminDirectoryUser): OrgRoleUi {
  const raw = member.platformRole ?? member.role;
  const normalized = normalizePlatformRole(raw);
  if (normalized === PLATFORM_ROLE.ADMIN) return "admin";
  if (normalized === PLATFORM_ROLE.VIEWER) return "viewer";
  return "member";
}

function mapInvitePlatformRole(member: AdminDirectoryUser): "Admin" | "Member" | "Viewer" {
  const r = toDropdownRole(member);
  if (r === "admin") return "Admin";
  if (r === "viewer") return "Viewer";
  return "Member";
}

function formatOrgRole(role: string): string {
  const r = role.trim().toLowerCase();
  if (r === "owner") return "Owner";
  if (r === "admin") return "Admin";
  if (r === "viewer" || r === "guest") return "Viewer";
  if (r === "member") return "Member";
  return role.trim() || "—";
}

function peopleStatusFromRaw(raw: string): PeopleStatus {
  const s = raw.trim().toLowerCase();
  if (s === "active" || s === "accepted") return "Active";
  if (s === "pending" || s === "invited") return "Pending";
  if (s === "suspended") return "Suspended";
  // Revoke back-dates expiry; the API reports that as expired. Do not invent Revoked.
  if (s === "expired" || s === "revoked") return "Expired";
  return "Pending";
}

function statusBadgeClass(status: PeopleStatus): string {
  if (status === "Active") return "bg-green-100 text-green-800";
  if (status === "Pending") return "bg-blue-100 text-blue-800";
  if (status === "Suspended") return "bg-orange-100 text-orange-800";
  if (status === "Expired") return "bg-gray-100 text-gray-700";
  return "bg-gray-100 text-gray-700";
}

function readApiError(err: unknown, fallback: string): string {
  const e = err as {
    code?: string;
    message?: string;
    response?: { data?: { code?: string; message?: string } };
  };
  if (e?.response?.data) {
    return getApiErrorMessage(e.response.data) || fallback;
  }
  if (e?.code || e?.message) {
    return getApiErrorMessage({ code: e.code, message: e.message }) || fallback;
  }
  return fallback;
}

function inviteMatchesSearch(invite: OrgInviteListItem, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const hay = `${invite.name ?? ""} ${invite.email} ${invite.role}`.toLowerCase();
  return hay.includes(q);
}

function emptyStateCopy(opts: {
  filter: (typeof STATUS_FILTERS)[number];
  search: string;
}): string {
  const searched = Boolean(opts.search.trim());
  if (opts.filter === "Invited") {
    if (searched) return "No invitations match your search.";
    return "No pending invitations.";
  }
  if (searched) return "No users match your search.";
  if (opts.filter === "Active") return "No active members.";
  if (opts.filter === "Suspended") return "No suspended members.";
  return "No people to show.";
}

const STATUS_FILTERS = ["All", "Active", "Suspended", "Invited"] as const;
const PEOPLE_TABLE_COLUMNS = 11;

type AdministrationUsersPageProps = {
  /** When true, omit the page-level title (People & Teams shell owns it). */
  hidePageHeader?: boolean;
};

export default function AdministrationUsersPage({
  hidePageHeader = false,
}: AdministrationUsersPageProps = {}) {
  const { user: authUser } = useAuth();
  const orgId = authUser?.organizationId || authUser?.organization?.id || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminDirectoryUser[]>([]);
  const [invites, setInvites] = useState<OrgInviteListItem[]>([]);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<(typeof STATUS_FILTERS)[number]>("All");
  const [menuUserId, setMenuUserId] = useState<string | null>(null);
  const [seatLimit, setSeatLimit] = useState<number | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [resetLinkUserId, setResetLinkUserId] = useState<string | null>(null);
  const [teamMembershipUserId, setTeamMembershipUserId] = useState<string | null>(null);
  const [revokeInvite, setRevokeInvite] = useState<OrgInviteListItem | null>(null);

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    setError(null);
    const search = searchQuery.trim();
    const usersStatus =
      activeFilter === "Active" ? "active" : activeFilter === "Suspended" ? "suspended" : "all";

    try {
      const usersResult = await administrationApi.listUsers({
        page: 1,
        limit: 100,
        search: activeFilter === "Invited" ? undefined : search || undefined,
        status: usersStatus,
      });
      setUsers(usersResult.data);
      setSeatLimit(usersResult.seatLimit);
      setMemberCount(usersResult.memberCount);
    } catch {
      setError("Failed to load users.");
      setUsers([]);
      setSeatLimit(null);
      setMemberCount(0);
    }

    try {
      if (!orgId) {
        setInvites([]);
      } else {
        setInvites(await administrationApi.listOrgInvites(orgId));
      }
    } catch (err: unknown) {
      setInvites([]);
      if (activeFilter === "Invited" || activeFilter === "All") {
        setError(readApiError(err, "Failed to load invitations."));
      }
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeFilter, orgId]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void loadDirectory();
    }, searchQuery.trim() ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [loadDirectory, searchQuery]);

  const pendingInvites = useMemo(() => {
    const q = searchQuery.trim();
    return invites.filter((invite) => {
      if (invite.status.toLowerCase() !== "pending") return false;
      return inviteMatchesSearch(invite, q);
    });
  }, [invites, searchQuery]);

  const visibleMembers = activeFilter === "Invited" ? [] : users;
  const visibleInvites =
    activeFilter === "Active" || activeFilter === "Suspended" ? [] : pendingInvites;

  const nonOwnerAdminCount = useMemo(
    () =>
      users.filter((u) => !u.isOwner && normalizePlatformRole(u.platformRole ?? u.role) === PLATFORM_ROLE.ADMIN)
        .length,
    [users],
  );

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  const editUser = useMemo(() => users.find((u) => u.id === editUserId) ?? null, [users, editUserId]);

  const resetLinkUser = useMemo(
    () => users.find((u) => u.id === resetLinkUserId) ?? null,
    [users, resetLinkUserId],
  );

  const teamMembershipUser = useMemo(
    () => users.find((u) => u.id === teamMembershipUserId) ?? null,
    [users, teamMembershipUserId],
  );

  const callerIsOwner = useMemo(() => {
    const caller = users.find((u) => u.id === authUser?.id);
    if (!caller) return false;
    return Boolean(caller.isOwner || caller.role === LEGACY_ORG_ROLE.OWNER);
  }, [users, authUser?.id]);

  function isLastOrgAdmin(member: AdminDirectoryUser): boolean {
    if (member.isOwner) return false;
    const pr = normalizePlatformRole(member.platformRole ?? member.role);
    if (pr !== PLATFORM_ROLE.ADMIN) return false;
    return nonOwnerAdminCount <= 1;
  }

  async function applyRoleChange(userId: string, role: OrgRoleUi) {
    setBusyUserId(userId);
    try {
      await administrationApi.changeUserRole(userId, role);
      await loadDirectory();
    } catch (err: unknown) {
      setError(readApiError(err, "Failed to change role."));
    } finally {
      setBusyUserId(null);
    }
  }

  async function onDeactivateUser(userId: string) {
    if (!window.confirm("Deactivate this person’s organization access?")) return;
    setBusyUserId(userId);
    setMenuUserId(null);
    try {
      await administrationApi.deactivateUser(userId, "Deactivated from People page");
      setEditUserId(null);
      setSelectedUserId(null);
      await loadDirectory();
    } catch (err: unknown) {
      setError(readApiError(err, "Failed to deactivate user."));
    } finally {
      setBusyUserId(null);
    }
  }

  async function onReinvite(member: AdminDirectoryUser) {
    setBusyUserId(member.id);
    try {
      const pr = mapInvitePlatformRole(member);
      const orgRole = pr === "Viewer" ? "VIEWER" : "MEMBER";
      await administrationApi.inviteOrgUserV1({
        email: member.email.trim().toLowerCase(),
        orgRole,
      });
      setNotice("Invitation sent.");
      await loadDirectory();
    } catch (err: unknown) {
      setError(readApiError(err, "Reinvite failed."));
    } finally {
      setBusyUserId(null);
    }
  }

  /**
   * J1-INVITE-LINK-1: copy the accept link of a pending invite while mail is frozen. The link
   * is read when asked for and never stored or shown; a server refusal (no longer pending, no
   * retrievable link) is shown as its own message, and nothing is copied.
   */
  async function onCopyInviteLink(invite: OrgInviteListItem): Promise<void> {
    if (!orgId) return;
    setBusyUserId(invite.id);
    setError(null);
    try {
      const { link } = await administrationApi.getOrgInviteLink(orgId, invite.id);
      await navigator.clipboard.writeText(link);
      toast.success(`Invite link for ${invite.email} copied.`);
    } catch (err: unknown) {
      setError(readApiError(err, "Could not copy the invite link."));
    } finally {
      setBusyUserId(null);
    }
  }

  async function onResendInvite(invite: OrgInviteListItem) {
    if (!orgId) return;
    setBusyUserId(invite.id);
    setError(null);
    try {
      await administrationApi.resendOrgInvite(orgId, invite.id);
      setNotice("Invitation resent.");
      await loadDirectory();
    } catch (err: unknown) {
      setError(readApiError(err, "Failed to resend invitation."));
    } finally {
      setBusyUserId(null);
    }
  }

  async function onConfirmRevoke() {
    if (!orgId || !revokeInvite) return;
    const invite = revokeInvite;
    setBusyUserId(invite.id);
    setError(null);
    try {
      await administrationApi.revokeOrgInvite(orgId, invite.id);
      setRevokeInvite(null);
      setNotice("Invitation revoked.");
      await loadDirectory();
    } catch (err: unknown) {
      setError(readApiError(err, "Failed to revoke invitation."));
    } finally {
      setBusyUserId(null);
    }
  }

  function onInviteSuccess() {
    setNotice("Invitation sent.");
    if (activeFilter !== "Invited") {
      setActiveFilter("Invited");
      return;
    }
    void loadDirectory();
  }

  const emptyCopy = emptyStateCopy({
    filter: activeFilter,
    search: searchQuery,
  });
  const hasRows = visibleMembers.length > 0 || visibleInvites.length > 0;
  const headerCountCopy =
    activeFilter === "Invited"
      ? pluralize(visibleInvites.length, "invitation")
      : pluralize(memberCount, "member");

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {!hidePageHeader ? (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">People</h1>
            <p className="mt-1 text-sm text-gray-500">
              {headerCountCopy} · {seatLimit != null && Number.isFinite(seatLimit) ? seatLimit : "∞"} seats
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Organization roles use Admin, Member, and Viewer. Workspace membership counts are managed per
              workspace.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {headerCountCopy} · {seatLimit != null && Number.isFinite(seatLimit) ? seatLimit : "∞"} seats
          </p>
        )}
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="shrink-0 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          data-testid="invite-people-button"
        >
          Invite people
        </button>
      </header>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="text-sm text-green-800" role="status" aria-live="polite">
          {notice}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <input
          type="text"
          placeholder="Search by name or email…"
          className="w-full flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-indigo-500"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search people"
        />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                activeFilter === filter ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Org role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Teams</th>
                <th className="px-4 py-3">Last active</th>
                <th className="px-4 py-3">Invited by</th>
                <th className="px-4 py-3">Invited at</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Workspaces</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-8 text-sm text-gray-500" colSpan={PEOPLE_TABLE_COLUMNS}>
                    Loading users…
                  </td>
                </tr>
              ) : !hasRows ? (
                <tr>
                  <td className="px-4 py-8 text-sm text-gray-500" colSpan={PEOPLE_TABLE_COLUMNS}>
                    {emptyCopy}
                  </td>
                </tr>
              ) : (
                <>
                  {visibleMembers.map((member) => {
                    const dropdownRole = toDropdownRole(member);
                    const lastAdmin = isLastOrgAdmin(member);
                    const wsCount = member.workspaceAccess?.length ?? 0;
                    const status = peopleStatusFromRaw(member.status);
                    return (
                      <tr
                        key={`member:${member.id}`}
                        className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50/80 ${
                          selectedUserId === member.id ? "bg-indigo-50/60" : ""
                        }`}
                        onClick={() => setSelectedUserId((id) => (id === member.id ? null : member.id))}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white"
                              aria-hidden
                            >
                              {initials(member)}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-gray-900">{member.name}</div>
                              {member.isOwner ? (
                                <div className="text-[11px] font-medium text-amber-700">Organization owner</div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="max-w-[200px] truncate px-4 py-3 text-sm text-gray-600">{member.email}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {member.isOwner ? (
                            <span className="text-sm font-medium text-gray-800">Owner</span>
                          ) : (
                            <RoleSelector
                              kind="org"
                              aria-label={`Organization role for ${member.name}`}
                              value={dropdownRole}
                              disabled={busyUserId === member.id || lastAdmin}
                              disabledReason={
                                lastAdmin ? "Cannot demote — last organization admin. Promote another admin first." : ""
                              }
                              onChange={(next) => {
                                if (next === dropdownRole) return;
                                void applyRoleChange(member.id, next);
                              }}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(status)}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {(member.teams?.length ?? 0) > 0
                            ? member.teams.map((t) => t.name).join(", ")
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {formatRelative(member.lastActiveAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <button
                            type="button"
                            className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUserId(member.id);
                            }}
                          >
                            {wsCount}
                          </button>
                        </td>
                        <td className="relative px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            disabled={busyUserId === member.id}
                            className="inline-flex rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-haspopup="menu"
                            aria-expanded={menuUserId === member.id}
                            onClick={() => setMenuUserId((id) => (id === member.id ? null : member.id))}
                          >
                            <MoreHorizontal className="h-5 w-5" aria-hidden />
                            <span className="sr-only">Open actions for {member.name}</span>
                          </button>
                          {menuUserId === member.id ? (
                            <div
                              className="absolute right-4 z-[60] mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 text-left shadow-lg"
                              role="menu"
                            >
                              {!member.isOwner ? (
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  onClick={() => {
                                    setEditUserId(member.id);
                                    setMenuUserId(null);
                                  }}
                                >
                                  Edit details…
                                </button>
                              ) : null}
                              <button
                                type="button"
                                role="menuitem"
                                className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                onClick={() => {
                                  setTeamMembershipUserId(member.id);
                                  setMenuUserId(null);
                                }}
                              >
                                Manage team membership…
                              </button>
                              {canOfferSendResetLink({
                                callerId: authUser?.id,
                                callerIsOwner,
                                target: member,
                              }) ? (
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  onClick={() => {
                                    setResetLinkUserId(member.id);
                                    setMenuUserId(null);
                                  }}
                                >
                                  Send reset link
                                </button>
                              ) : null}
                              {member.status === "invited" ? (
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  onClick={() => void onReinvite(member)}
                                >
                                  Reinvite
                                </button>
                              ) : null}
                              {!member.isOwner ? (
                                <button
                                  type="button"
                                  role="menuitem"
                                  disabled={isLastOrgAdmin(member)}
                                  title={
                                    isLastOrgAdmin(member)
                                      ? "Cannot deactivate — last organization admin."
                                      : undefined
                                  }
                                  className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  onClick={() => {
                                    if (isLastOrgAdmin(member)) return;
                                    void onDeactivateUser(member.id);
                                  }}
                                >
                                  Deactivate access
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                  {visibleInvites.map((invite) => {
                    const displayName = invite.name || invite.email;
                    const status = peopleStatusFromRaw(invite.status);
                    return (
                      <tr key={`invite:${invite.id}`} className="border-b border-gray-100 hover:bg-gray-50/80">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-500 text-xs font-semibold text-white"
                              aria-hidden
                            >
                              {initialsFromName(displayName, invite.email)}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-gray-900">{displayName}</div>
                            </div>
                          </div>
                        </td>
                        <td className="max-w-[200px] truncate px-4 py-3 text-sm text-gray-600">{invite.email}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{formatOrgRole(invite.role)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(status)}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{invite.invitedByLabel}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {formatAbsolute(invite.invitedAt)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {formatAbsolute(invite.expiresAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              size="xs"
                              variant="outline"
                              disabled={busyUserId === invite.id}
                              onClick={() => void onCopyInviteLink(invite)}
                              data-testid={`copy-invite-link-${invite.id}`}
                            >
                              Copy invite link
                            </Button>
                            <Button
                              type="button"
                              size="xs"
                              variant="outline"
                              disabled={busyUserId === invite.id}
                              onClick={() => void onResendInvite(invite)}
                            >
                              Resend
                            </Button>
                            <Button
                              type="button"
                              size="xs"
                              variant="danger"
                              disabled={busyUserId === invite.id}
                              onClick={() => setRevokeInvite(invite)}
                            >
                              Revoke
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedUser ? (
        <section
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          aria-label={`Workspace memberships for ${selectedUser.name}`}
        >
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Workspace memberships</h2>
              <p className="text-xs text-gray-500">{selectedUser.name}</p>
            </div>
            <button
              type="button"
              className="text-xs font-medium text-gray-600 hover:text-gray-900"
              onClick={() => setSelectedUserId(null)}
            >
              Close panel
            </button>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-2 pr-4">Workspace</th>
                  <th className="py-2 pr-4">Access</th>
                </tr>
              </thead>
              <tbody>
                {(selectedUser.workspaceAccess ?? []).length === 0 ? (
                  <tr>
                    <td className="py-3 text-gray-600" colSpan={2}>
                      No workspace memberships recorded for this user.
                    </td>
                  </tr>
                ) : (
                  selectedUser.workspaceAccess!.map((w) => (
                    <tr key={w.workspaceId} className="border-t border-gray-100">
                      <td className="py-2 pr-4 text-gray-900">{w.workspaceName}</td>
                      <td className="py-2 pr-4 text-gray-700">{w.accessLevel}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {menuUserId ? (
        <button
          type="button"
          className="fixed inset-0 z-[50] cursor-default bg-transparent"
          aria-label="Close actions menu"
          onClick={() => setMenuUserId(null)}
        />
      ) : null}

      <InviteOrgMemberDialog isOpen={inviteOpen} onClose={() => setInviteOpen(false)} onSuccess={onInviteSuccess} />

      <EditOrgMemberDialog
        member={editUser}
        isOpen={Boolean(editUser)}
        onClose={() => setEditUserId(null)}
        dropdownRole={editUser ? toDropdownRole(editUser) : "member"}
        roleDisabled={editUser ? isLastOrgAdmin(editUser) : false}
        roleDisabledReason={editUser && isLastOrgAdmin(editUser) ? "Cannot demote — last organization admin." : undefined}
        deactivateBlocked={editUser ? isLastOrgAdmin(editUser) : false}
        deactivateBlockedReason={
          editUser && isLastOrgAdmin(editUser) ? "Cannot deactivate — last organization admin." : undefined
        }
        busy={busyUserId === editUser?.id}
        onRoleChange={(role) => {
          if (!editUser || editUser.isOwner) return;
          void applyRoleChange(editUser.id, role);
        }}
        onDeactivate={() => editUser && void onDeactivateUser(editUser.id)}
        onReinvite={editUser && editUser.status === "invited" ? () => void onReinvite(editUser) : undefined}
      />

      <SendResetLinkDialog
        isOpen={Boolean(resetLinkUserId)}
        onClose={() => setResetLinkUserId(null)}
        userId={resetLinkUserId}
        userLabel={resetLinkUser?.name ?? resetLinkUser?.email ?? null}
      />

      {teamMembershipUser ? (
        <ManageTeamMembershipDialog
          user={teamMembershipUser}
          open={Boolean(teamMembershipUser)}
          onClose={() => setTeamMembershipUserId(null)}
          onUpdated={() => {
            void loadDirectory();
          }}
        />
      ) : null}

      <Modal
        isOpen={Boolean(revokeInvite)}
        onClose={() => setRevokeInvite(null)}
        title="Revoke invitation?"
        size="sm"
      >
        <p className="text-sm text-gray-700">
          Revoke the invitation to{" "}
          <span className="font-medium">{revokeInvite?.email}</span>? They will not be able to join
          with this link.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setRevokeInvite(null)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={busyUserId === revokeInvite?.id}
            onClick={() => void onConfirmRevoke()}
          >
            Revoke invitation
          </Button>
        </div>
      </Modal>
    </div>
  );
}
